import { isUndefined } from 'lodash-es';
import {
  type PropsWithChildren,
  type MouseEvent as ReactMouseEvent,
} from 'react';

import { InteractiveChartTooltip } from '../../../../core/components/tooltip/InteractiveChartTooltip/components/InteractiveChartTooltip.js';
import { VirtualEstimatedSize } from '../../../../core/components/tooltip/InteractiveChartTooltip/types/virtual-estimated-size.js';
import { useScrollResponsiveChartBounds } from '../../../../core/hooks/useScrollResponsiveChartBounds.js';
import { calculateAdjustedTooltipPosition } from '../../../../core/utils/calculate-adjusted-tooltip-position.js';
import {
  useMousePositionState,
  useTooltipState,
} from '../../../hooks/store/selectors.js';
import { useCategoricalBarChartOverlayRef } from '../../../hooks/useCategoricalBarChartOverlayRef.js';

interface CategoricalBarChartTooltipWrapperProps {
  onMouseLeave: (event?: ReactMouseEvent) => void;
  onScrollContent: (event?: Event) => void;
  estimatedItemSize: VirtualEstimatedSize;
}

const ADJUSTED_LAYOUT_OFFSET = { x: -10, y: 30 };

export const CategoricalBarChartTooltipWrapper = ({
  onMouseLeave,
  onScrollContent,
  estimatedItemSize,
  children,
}: PropsWithChildren<CategoricalBarChartTooltipWrapperProps>) => {
  const { pinnedTooltip } = useTooltipState();
  const mousePosition = useMousePositionState();
  const chartRef = useCategoricalBarChartOverlayRef();
  const bounds = useScrollResponsiveChartBounds(chartRef, pinnedTooltip)();

  if (isUndefined(mousePosition?.absolute)) {
    return null;
  }

  const adjustedPosition = calculateAdjustedTooltipPosition(
    bounds,
    mousePosition,
    ADJUSTED_LAYOUT_OFFSET,
  );

  if (!adjustedPosition) {
    return null;
  }

  return (
    <InteractiveChartTooltip
      position={adjustedPosition}
      pinned={pinnedTooltip}
      itemSize={estimatedItemSize}
      updatePositionWhenPinned={true}
      onMouseLeave={onMouseLeave}
      onScrollContent={onScrollContent}
    >
      {children}
    </InteractiveChartTooltip>
  );
};
